<template>
  <div
    class="background-task-card"
    v-on:mouseover="mouseOver"
    v-on:mouseleave="mouseLeave"
  >
    <div
      class="p-4 flex items-center justify-between"
      :class="{ completed: isCompletedTask }"
    >
      <div class="flex items-center w-full">
        <!-- 種別アイコン -->
        <div class="mr-4">
          <feather-icon
            :icon="taskCategory.icon"
            svgClasses="h-6 w-6"
            class="p-2 category-icon"
            :class="categoryIconClass"
          />
        </div>

        <div class="w-full h-full">
          <!-- タスク情報  -->
          <div class="flex items-center justify-between">
            <!-- タスク情報 左 -->
            <div class="flex items-center">
              <div class="task-category whitespace-nowrap">
                {{ taskCategory.name }}
              </div>
              <div @click="openTask()">
                <div
                  class="flex items-center pl-4 w-16 sm:w-48 lg:w-64 xl:w-full"
                  :class="{ 'cursor-pointer': clickable }"
                >
                  <feather-icon
                    icon="FileTextIcon"
                    svgClasses="h-4 w-4"
                    class="mr-2"
                  />
                  <vx-tooltip :text="task.name" class="truncate ...">
                    <span>
                      {{ task.name }}
                    </span>
                  </vx-tooltip>
                </div>
              </div>

              <vx-tooltip text="ファイルを開く">
                <feather-icon
                  v-if="clickable"
                  icon="ExternalLinkIcon"
                  svgClasses="h-4 w-4"
                  class="p-1 rounded ml-2 cursor-pointer"
                  @click="openTaskLink()"
                />
              </vx-tooltip>
            </div>

            <!-- タスク情報 右 -->
            <div class="flex items-center">
              <div class="task-date mx-6">{{ fileSize }}</div>
              <feather-icon
                icon="ClockIcon"
                svgClasses="h-4 w-4"
                class="mr-2"
                style=""
              />
              <div class="task-date">{{ taskDate }}</div>

              <div class="flex items-center ml-2" v-show="isCompletedTask">
                <div class="icon-button">
                  <vx-tooltip
                    text="タスク情報"
                    position="top"
                    class="flex items-center"
                  >
                    <feather-icon
                      icon="InfoIcon"
                      svgClasses="h-5 w-5"
                      @click="toggleDetail()"
                    />
                  </vx-tooltip>
                </div>
                <div class="icon-button">
                  <vx-tooltip
                    text="タスク削除"
                    position="top"
                    class="flex items-center"
                  >
                    <feather-icon
                      icon="TrashIcon"
                      svgClasses="h-5 w-5"
                      class="m-0 p-0"
                      @click="toggleDelete()"
                    />
                  </vx-tooltip>
                </div>
              </div>
            </div>
          </div>

          <!-- タスク進捗状況 -->
          <div v-if="!isCompletedTask">
            <!-- プログレスバー -->
            <div>
              <vs-progress
                :percent="progressPercent"
                :color="taskColor"
                :indeterminate="isIndeterminate"
                :height="6"
              ></vs-progress>
            </div>
            <!-- ステータス -->
            <div class="progress-text">
              <span class="mr-4" :class="progressTextClass"> 進行中 </span>
              <span class="mr-4" v-show="showPercentText">
                {{ task.progress }}%
              </span>
              <!-- 残り時間 -->
              <span class="mr-4" v-show="hasRemainingTime">
                {{ remainingTimeText() }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { UnitUtil } from "@/utils";
import { DeviceInfoMixin, ColorMixin, TaskManagerMixin } from "@/mixins";
import moment from "moment-timezone";
import 'moment/locale/ja'
import { createNamespacedHelpers } from "vuex";
const { mapMutations: mapMutationsOfTaskManager,
  mapActions: mapActionsOfTaskManager } = createNamespacedHelpers("enterpriseBackgroundTaskManager");
import { storageDocuments } from "@/firebase/firebaseConfig";
import { VESSEL_DATAIL_TAB, TASK_MANAGER_CATEGORY, TASK_MANAGER_STATUS } from "@/config/const";
import _ from "lodash";


export default {
  mixins: [DeviceInfoMixin, ColorMixin, TaskManagerMixin],
  props: {
    task: {
      required: true,
      type: Object,
    }
  },
  data() {
    return {
      isHovered: false,
    }
  },
  computed: {
    taskCategory() {
      return this.getCategoryItems(this.task.type)
    },
    isIndeterminate() {
      return this.task.type === TASK_MANAGER_CATEGORY.DOWNLOAD
    },
    showPercentText() {
      return this.task.type === TASK_MANAGER_CATEGORY.UPLOAD
    },
    progressPercent() {
      if (this.isIndeterminate) return 0;
      return this.task.progress
    },
    isCompletedTask() {
      return this.task.status === TASK_MANAGER_STATUS.COMPLETED
    },
    taskDate() {
      if (this.isCompletedTask) return moment(this.task.updated_at).format("YYYY-MM-DD HH:mm")
      return moment(this.task.created_at).fromNow()
    },

    fileSize() {
      if (!this.task.bytes) return "";
      return UnitUtil.formatBytes(this.task.bytes, 0)
    },

    clickable() {
      if (!this.task.url) return false;
      return this.isCompletedTask && this.task.url.length > 0;
    },

    categoryIconClass() {
      let categoryClass = `category-icon--${ this.taskCategory.cssClassSuffix }`;

      if (!this.isCompletedTask) {
        categoryClass += `__progress`
      }
      return categoryClass;
    },

    progressTextClass() {
      return `progress-text progress-text--${ this.taskCategory.cssClassSuffix }`
    },

    taskColor() {
      return this.TASK_COLOR[this.taskCategory.color]
    },

    showHoverButtonArea() {
      return this.isHovered && this.isCompletedTask
    },

    hasRemainingTime() {
      return this.task.transferred && this.task.transferred > 0;
    },

  },
  methods: {
    ...mapActionsOfTaskManager(["removeTask"]),
    ...mapMutationsOfTaskManager(["SET_DETAIL_SIDEBAR_ACTIVE", "SET_SELECTED_TASK"]),

    mouseOver() {
      this.isHovered = true;
    },
    mouseLeave() {
      this.isHovered = false;
    },

    remainingTimeText() {
      const remainingTimeSeconds = this.calcRemainingTime();
      if (!remainingTimeSeconds || remainingTimeSeconds < 1) return "";
      const remainingTime = moment.duration({ 'seconds': remainingTimeSeconds });
      const hours = remainingTime.hours() > 0 ? `${ remainingTime.hours() }時間` : "";
      const minutes = remainingTime.minutes() > 0 ? `${ remainingTime.minutes() }分` : "";
      const seconds = remainingTime.seconds() > 0 ? `${ remainingTime.seconds() }秒` : "";
      return `残り${ hours }${ minutes }${ seconds }`;
    },

    calcUploadBytePerSecond() {
      const uploadTime = moment().diff(moment(this.task.created_at), "seconds");
      if (uploadTime < 1) return;

      const uploadBytePerSecond = this.task.transferred / uploadTime;
      return uploadBytePerSecond;
    },

    calcRemainingTime() {
      const uploadBytePerSecond = this.calcUploadBytePerSecond();
      if (!uploadBytePerSecond) return;

      const remainingSize = this.task.bytes - this.task.transferred;
      const remainingTime = remainingSize / uploadBytePerSecond;

      return remainingTime;
    },

    toggleDetail() {
      this.SET_SELECTED_TASK(this.task);
      this.SET_DETAIL_SIDEBAR_ACTIVE(true)
    },

    async openTask() {
      if (!this.isCompletedTask) return;
      this.openDocumentManager();
    },

    openDocumentManager() {
      if (!this.task.vesselId || this.task.vesselId.length === 0) return;

      const vesselId = this.task.vesselId
      this.$router.push({
        name: "enterprise-management-vessel-view",
        params: { vesselId: vesselId },
        query: { path: this.task.url, tab: VESSEL_DATAIL_TAB.DOCUMENT }
      });
    },

    async openTaskLink() {
      if (!this.isCompletedTask) return;
      const url = await storageDocuments.child(this.task.url).getDownloadURL();
      window.open(url, '_blank');
    },

    toggleDelete() {
      const msg = `${ this.taskCategory.name }「${ this.task.name }」を完全に削除しますか？削除後は復旧できません`
      this.$vs.dialog({
        type: "confirm",
        color: "danger",
        title: `タスクの削除`,
        text: msg,
        acceptText: "削除する",
        cancelText: "キャンセル",
        accept: this.acceptDelete
      });
    },

    async acceptDelete() {
      const _category = _.cloneDeep(this.taskCategory)
      const deleteItem = _.cloneDeep(this.task);
      try {
        await this.removeTask(deleteItem.id)
        this.$vs.notify({
          title: `${ _category.name }`,
          text: `「${ deleteItem.name }」を削除しました`,
          color: "success",
          iconPack: "feather",
          icon: "icon-check"
        });
        this.$vs.loading.close();
      } catch (error) {
        console.error(error);
        this.$vs.notify({
          title: `${ _category.name }`,
          text: `「${ deleteItem.name }」の削除に失敗しました`,
          color: "danger",
          iconPack: "feather",
          icon: "icon-x-square"
        });
      }
    },
  }

}
</script>

<style lang="scss">
@import "@/assets/scss/aisea/_variables.scss";
@import "src/assets/scss/aisea/task-manager.scss";

.background-task-card {
  position: relative;
  color: $aisea-gray;
  .con-div {
    margin: 0;
  }

  .con-vs-tooltip {
    margin: 0;
    padding: 0;
  }

  .completed {
    background: $taskmanager-completed-bg;
  }

  // .hover-button-area {
  //   // position: absolute;
  //   // top: 50%;
  //   // right: 0;
  //   // transform: translateY(-50%);
  //   // z-index: 1;

  .icon-button {
    display: flex;
    align-items: center;
    color: $aisea-gray;
    padding: 8px;
    border-radius: 6px;
    cursor: pointer;

    &:hover {
      background-color: $aisea-light-gray;
    }
  }
  // }

  .category-icon {
    border-radius: 100%;
    border: solid 2px $taskmanager-border-gray;
    background-color: $aisea-pure-white;

    &--upload {
      color: $taskmanager-green;

      &__progress {
        color: $taskmanager-green;
        border: solid 2px $taskmanager-light-green;
        background-color: $taskmanager-light-green;
      }
    }

    &--download {
      color: $taskmanager-blue;
      &__progress {
        color: $taskmanager-blue;
        border: solid 2px $taskmanager-light-blue;
        background-color: $taskmanager-light-blue;
      }
    }
  }

  .progress-text {
    font-size: 12px;

    &--upload {
      color: $taskmanager-green;
    }

    &--download {
      color: $taskmanager-blue;
    }
  }

  .task-category {
    font-weight: 600;
  }

  .task-name {
    font-size: 14px;
  }

  .task-filesize {
    font-size: 12px;
  }

  .task-date {
    font-size: 12px;
  }

  .file-link {
    color: $aisea-gray;
  }

  .open-label {
    color: $aisea-blue;
    font-weight: 600;
    font-size: 14px;
  }

  .card-pause {
    background-color: $aisea-white;
  }

  .pause-button {
    color: $aisea-mouse-gray;
    border-radius: 4px;

    &:hover {
      background-color: $aisea-white;
    }
  }
}
</style>